package com.banyan.flightviewer.data

/**
 * Airport lookup utilities for Air Traffic Monitor.
 *
 * Provides functions to lookup airport names from IATA/ICAO codes.
 */
object AirportDatabase {

    /**
     * Comprehensive airport database (IATA codes only for US airports).
     * ICAO codes starting with 'K' (US airports) are automatically handled by stripping the 'K'.
     */
    private val airports: Map<String, String> = mapOf(
        // Major US airports
        "ATL" to "Hartsfield-Jackson Atlanta Intl",
        "LAX" to "Los Angeles Intl",
        "ORD" to "Chicago O'Hare Intl",
        "DFW" to "Dallas/Fort Worth Intl",
        "DEN" to "Denver Intl",
        "JFK" to "John F. Kennedy Intl",
        "SFO" to "San Francisco Intl",
        "SEA" to "Seattle-Tacoma Intl",
        "LAS" to "Las Vegas McCarran Intl",
        "MCO" to "Orlando Intl",
        "EWR" to "Newark Liberty Intl",
        "MIA" to "Miami Intl",
        "PHX" to "Phoenix Sky Harbor Intl",
        "IAH" to "George Bush Intercontinental",
        "BOS" to "Boston Logan Intl",
        "MSP" to "Minneapolis-St. Paul Intl",
        "DTW" to "Detroit Metropolitan Wayne County",
        "PHL" to "Philadelphia Intl",
        "LGA" to "LaGuardia",
        "BWI" to "Baltimore/Washington Intl",
        "SLC" to "Salt Lake City Intl",
        "SAN" to "San Diego Intl",
        "IAD" to "Washington Dulles Intl",
        "DCA" to "Ronald Reagan Washington National",
        "TPA" to "Tampa Intl",
        "PDX" to "Portland Intl",
        "HNL" to "Honolulu Intl",
        "STL" to "St. Louis Lambert Intl",
        "MDW" to "Chicago Midway Intl",
        "BNA" to "Nashville Intl",
        "AUS" to "Austin-Bergstrom Intl",
        "OAK" to "Oakland Intl",
        "SJC" to "San Jose Intl",
        "MSY" to "Louis Armstrong New Orleans Intl",
        "RDU" to "Raleigh-Durham Intl",
        "SNA" to "John Wayne Airport-Orange County",
        "SAT" to "San Antonio Intl",
        "SMF" to "Sacramento Intl",
        "DAL" to "Dallas Love Field",
        "CLE" to "Cleveland Hopkins Intl",
        "PIT" to "Pittsburgh Intl",
        "IND" to "Indianapolis Intl",
        "CMH" to "John Glenn Columbus Intl",
        "MCI" to "Kansas City Intl",
        "CVG" to "Cincinnati/Northern Kentucky Intl",
        "JAX" to "Jacksonville Intl",
        "ABQ" to "Albuquerque Intl Sunport",
        "MKE" to "Milwaukee Mitchell Intl",
        "OMA" to "Eppley Airfield",
        "RNO" to "Reno-Tahoe Intl",
        "TUS" to "Tucson Intl",
        "BDL" to "Bradley Intl",
        "BUF" to "Buffalo Niagara Intl",
        "BUR" to "Hollywood Burbank",
        "ONT" to "Ontario Intl",
        "ANC" to "Ted Stevens Anchorage Intl",
        "FLL" to "Fort Lauderdale-Hollywood Intl",
        "SRQ" to "Sarasota-Bradenton Intl",
        "RSW" to "Southwest Florida Intl",
        "PBI" to "Palm Beach Intl",
        "PNS" to "Pensacola Intl",
        "ECP" to "Northwest Florida Beaches Intl",
        "VPS" to "Destin-Fort Walton Beach",
        "TLH" to "Tallahassee Intl",
        "PIE" to "St. Petersburg-Clearwater Intl",
        "DAB" to "Daytona Beach Intl",
        "GNV" to "Gainesville Regional",
        "EYW" to "Key West Intl",
        "MLB" to "Melbourne Orlando Intl",
        "PGD" to "Punta Gorda",

        // More US East Coast
        "ORF" to "Norfolk Intl",
        "RIC" to "Richmond Intl",
        "ROC" to "Greater Rochester Intl",
        "SYR" to "Syracuse Hancock Intl",
        "ALB" to "Albany Intl",
        "PVD" to "T.F. Green Airport",
        "MHT" to "Manchester-Boston Regional",
        "PWM" to "Portland Intl Jetport",
        "BTV" to "Burlington Intl",
        "CAK" to "Akron-Canton",
        "DAY" to "Dayton Intl",
        "LEX" to "Blue Grass Airport",
        "SDF" to "Louisville Muhammad Ali Intl",
        "GSP" to "Greenville-Spartanburg Intl",
        "CHS" to "Charleston Intl",
        "SAV" to "Savannah/Hilton Head Intl",
        "MYR" to "Myrtle Beach Intl",
        "CLT" to "Charlotte Douglas Intl",
        "GSO" to "Piedmont Triad Intl",

        // More US West Coast
        "BOI" to "Boise Airport",
        "GEG" to "Spokane Intl",
        "FAT" to "Fresno Yosemite Intl",
        "SBA" to "Santa Barbara Municipal",
        "PSP" to "Palm Springs Intl",
        "LGB" to "Long Beach Airport",

        // More US Central
        "DSM" to "Des Moines Intl",
        "ICT" to "Wichita Dwight D. Eisenhower",
        "TUL" to "Tulsa Intl",
        "OKC" to "Will Rogers World Airport",
        "LIT" to "Bill and Hillary Clinton Natl",
        "MEM" to "Memphis Intl",
        "BHM" to "Birmingham-Shuttlesworth Intl",
        "HSV" to "Huntsville Intl",
        "MGM" to "Montgomery Regional",
        "MOB" to "Mobile Regional",
        "GPT" to "Gulfport-Biloxi Intl",
        "BTR" to "Baton Rouge Metropolitan",
        "SHV" to "Shreveport Regional",
        "ELP" to "El Paso Intl",
        "MAF" to "Midland Intl Air and Space Port",
        "LBB" to "Lubbock Preston Smith Intl",
        "AMA" to "Rick Husband Amarillo Intl",
        "CRP" to "Corpus Christi Intl",
        "HRL" to "Valley Intl Airport",
        "BRO" to "Brownsville South Padre Island Intl",
        "GRR" to "Gerald R. Ford Intl",
        "FNT" to "Bishop Intl Airport",
        "LAN" to "Capital Region Intl",
        "GRB" to "Green Bay-Austin Straubel Intl",
        "MSN" to "Dane County Regional",
        "FAR" to "Hector Intl Airport",
        "FSD" to "Sioux Falls Regional",
        "BIL" to "Billings Logan Intl",

        // Alaska and Hawaii (non-K ICAO codes kept for these)
        "PANC" to "Ted Stevens Anchorage Intl",
        "PHNL" to "Honolulu Intl",

        // Major international airports
        "LHR" to "London Heathrow",
        "EGLL" to "London Heathrow",
        "CDG" to "Paris Charles de Gaulle",
        "LFPG" to "Paris Charles de Gaulle",
        "FRA" to "Frankfurt",
        "EDDF" to "Frankfurt",
        "AMS" to "Amsterdam Schiphol",
        "EHAM" to "Amsterdam Schiphol",
        "MAD" to "Madrid-Barajas",
        "LEMD" to "Madrid-Barajas",
        "FCO" to "Rome Fiumicino",
        "LIRF" to "Rome Fiumicino",
        "MUC" to "Munich",
        "EDDM" to "Munich",
        "BCN" to "Barcelona-El Prat",
        "LEBL" to "Barcelona-El Prat",
        "LGW" to "London Gatwick",
        "EGKK" to "London Gatwick",
        "ZRH" to "Zurich",
        "LSZH" to "Zurich",
        "VIE" to "Vienna Intl",
        "LOWW" to "Vienna Intl",
        "CPH" to "Copenhagen",
        "EKCH" to "Copenhagen",
        "DUB" to "Dublin",
        "EIDW" to "Dublin",
        "BRU" to "Brussels",
        "EBBR" to "Brussels",
        "LIS" to "Lisbon Portela",
        "LPPT" to "Lisbon Portela",
        "ARN" to "Stockholm Arlanda",
        "ESSA" to "Stockholm Arlanda",
        "OSL" to "Oslo Gardermoen",
        "ENGM" to "Oslo Gardermoen",
        "HEL" to "Helsinki-Vantaa",
        "EFHK" to "Helsinki-Vantaa",
        "IST" to "Istanbul",
        "LTFM" to "Istanbul",
        "ATH" to "Athens Intl",
        "LGAV" to "Athens Intl",
        "PRG" to "Prague V\u00e1clav Havel",
        "LKPR" to "Prague V\u00e1clav Havel",
        "WAW" to "Warsaw Chopin",
        "EPWA" to "Warsaw Chopin",

        // Canadian airports
        "YYZ" to "Toronto Pearson Intl",
        "CYYZ" to "Toronto Pearson Intl",
        "YVR" to "Vancouver Intl",
        "CYVR" to "Vancouver Intl",
        "YUL" to "Montr\u00e9al-Trudeau Intl",
        "CYUL" to "Montr\u00e9al-Trudeau Intl",
        "YYC" to "Calgary Intl",
        "CYYC" to "Calgary Intl",
        "YOW" to "Ottawa Macdonald-Cartier Intl",
        "CYOW" to "Ottawa Macdonald-Cartier Intl",

        // Mexican airports
        "MEX" to "Mexico City Intl",
        "MMMX" to "Mexico City Intl",
        "CUN" to "Canc\u00fan Intl",
        "MMUN" to "Canc\u00fan Intl",
        "GDL" to "Guadalajara Intl",
        "MMGL" to "Guadalajara Intl",

        // Caribbean
        "SJU" to "Luis Mu\u00f1oz Mar\u00edn Intl",
        "TJSJ" to "Luis Mu\u00f1oz Mar\u00edn Intl",

        // Asia-Pacific
        "HND" to "Tokyo Haneda",
        "RJTT" to "Tokyo Haneda",
        "NRT" to "Tokyo Narita",
        "RJAA" to "Tokyo Narita",
        "PEK" to "Beijing Capital Intl",
        "ZBAA" to "Beijing Capital Intl",
        "PVG" to "Shanghai Pudong Intl",
        "ZSPD" to "Shanghai Pudong Intl",
        "HKG" to "Hong Kong Intl",
        "VHHH" to "Hong Kong Intl",
        "SIN" to "Singapore Changi",
        "WSSS" to "Singapore Changi",
        "ICN" to "Seoul Incheon Intl",
        "RKSI" to "Seoul Incheon Intl",
        "BKK" to "Bangkok Suvarnabhumi",
        "VTBS" to "Bangkok Suvarnabhumi",
        "KUL" to "Kuala Lumpur Intl",
        "WMKK" to "Kuala Lumpur Intl",
        "SYD" to "Sydney Kingsford Smith",
        "YSSY" to "Sydney Kingsford Smith",
        "MEL" to "Melbourne",
        "YMML" to "Melbourne",
        "BNE" to "Brisbane",
        "YBBN" to "Brisbane",
        "AKL" to "Auckland",
        "NZAA" to "Auckland",

        // Middle East
        "DXB" to "Dubai Intl",
        "OMDB" to "Dubai Intl",
        "DOH" to "Hamad Intl",
        "OTHH" to "Hamad Intl",
        "AUH" to "Abu Dhabi Intl",
        "OMAA" to "Abu Dhabi Intl",

        // South America
        "GRU" to "S\u00e3o Paulo-Guarulhos Intl",
        "SBGR" to "S\u00e3o Paulo-Guarulhos Intl",
        "GIG" to "Rio de Janeiro-Gale\u00e3o Intl",
        "SBGL" to "Rio de Janeiro-Gale\u00e3o Intl",
        "EZE" to "Buenos Aires Ezeiza Intl",
        "SAEZ" to "Buenos Aires Ezeiza Intl",
        "BOG" to "Bogot\u00e1 El Dorado Intl",
        "SKBO" to "Bogot\u00e1 El Dorado Intl",
        "LIM" to "Lima Jorge Ch\u00e1vez Intl",
        "SPIM" to "Lima Jorge Ch\u00e1vez Intl",
        "SCL" to "Santiago Arturo Merino Ben\u00edtez Intl",
        "SCEL" to "Santiago Arturo Merino Ben\u00edtez Intl",
    )

    data class AirportLocation(val code: String, val name: String, val lat: Double, val lon: Double)

    /** Major airports with coordinates for map display. */
    private val airportLocations: List<AirportLocation> = listOf(
        // Top US airports
        AirportLocation("ATL", "Atlanta", 33.6407, -84.4277),
        AirportLocation("LAX", "Los Angeles", 33.9425, -118.4081),
        AirportLocation("ORD", "Chicago O'Hare", 41.9742, -87.9073),
        AirportLocation("DFW", "Dallas/Fort Worth", 32.8998, -97.0403),
        AirportLocation("DEN", "Denver", 39.8561, -104.6737),
        AirportLocation("JFK", "New York JFK", 40.6413, -73.7781),
        AirportLocation("SFO", "San Francisco", 37.6213, -122.3790),
        AirportLocation("SEA", "Seattle", 47.4502, -122.3088),
        AirportLocation("LAS", "Las Vegas", 36.0840, -115.1537),
        AirportLocation("MCO", "Orlando", 28.4312, -81.3081),
        AirportLocation("EWR", "Newark", 40.6895, -74.1745),
        AirportLocation("MIA", "Miami", 25.7959, -80.2870),
        AirportLocation("PHX", "Phoenix", 33.4373, -112.0078),
        AirportLocation("IAH", "Houston IAH", 29.9902, -95.3368),
        AirportLocation("BOS", "Boston", 42.3656, -71.0096),
        AirportLocation("MSP", "Minneapolis", 44.8848, -93.2223),
        AirportLocation("DTW", "Detroit", 42.2162, -83.3554),
        AirportLocation("PHL", "Philadelphia", 39.8744, -75.2424),
        AirportLocation("LGA", "New York LGA", 40.7769, -73.8740),
        AirportLocation("BWI", "Baltimore", 39.1774, -76.6684),
        AirportLocation("SLC", "Salt Lake City", 40.7884, -111.9778),
        AirportLocation("SAN", "San Diego", 32.7336, -117.1897),
        AirportLocation("IAD", "Washington Dulles", 38.9531, -77.4565),
        AirportLocation("DCA", "Washington Reagan", 38.8521, -77.0377),
        AirportLocation("TPA", "Tampa", 27.9756, -82.5333),
        AirportLocation("FLL", "Fort Lauderdale", 26.0742, -80.1506),
        AirportLocation("CLT", "Charlotte", 35.2140, -80.9431),
        AirportLocation("PDX", "Portland", 45.5898, -122.5951),
        AirportLocation("STL", "St. Louis", 38.7487, -90.3700),
        AirportLocation("BNA", "Nashville", 36.1263, -86.6774),
        AirportLocation("AUS", "Austin", 30.1975, -97.6664),
        AirportLocation("MSY", "New Orleans", 29.9934, -90.2580),
        AirportLocation("RDU", "Raleigh-Durham", 35.8776, -78.7875),
        AirportLocation("SNA", "Orange County", 33.6757, -117.8678),
        AirportLocation("MDW", "Chicago Midway", 41.7868, -87.7522),
        AirportLocation("HNL", "Honolulu", 21.3187, -157.9224),
        AirportLocation("ANC", "Anchorage", 61.1743, -149.9962),
        AirportLocation("RSW", "Fort Myers", 26.5362, -81.7552),
        AirportLocation("PBI", "West Palm Beach", 26.6832, -80.0956),
        AirportLocation("SRQ", "Sarasota", 27.3954, -82.5544),
        AirportLocation("PIE", "St. Petersburg", 27.9110, -82.6874),

        // Florida
        AirportLocation("JAX", "Jacksonville", 30.4941, -81.6879),
        AirportLocation("PNS", "Pensacola", 30.4734, -87.1866),
        AirportLocation("ECP", "Panama City Beach", 30.3580, -85.7956),
        AirportLocation("VPS", "Destin/Fort Walton", 30.4832, -86.5254),
        AirportLocation("TLH", "Tallahassee", 30.3965, -84.3503),
        AirportLocation("GNV", "Gainesville", 29.6901, -82.2718),
        AirportLocation("DAB", "Daytona Beach", 29.1799, -81.0581),
        AirportLocation("MLB", "Melbourne", 28.1028, -80.6453),
        AirportLocation("SFB", "Orlando Sanford", 28.7776, -81.2375),
        AirportLocation("PGD", "Punta Gorda", 26.9202, -81.9906),
        AirportLocation("VNC", "Venice", 27.0716, -82.4403),
        AirportLocation("EYW", "Key West", 24.5561, -81.7596),
        AirportLocation("APF", "Naples", 26.1526, -81.7753),
        AirportLocation("FXE", "Fort Lauderdale Exec", 26.1973, -80.1707),
        AirportLocation("OPF", "Miami Opa-locka", 25.9070, -80.2784),
        AirportLocation("TMB", "Miami Kendall-Tamiami", 25.6479, -80.4328),
        AirportLocation("BCT", "Boca Raton", 26.3785, -80.1077),
        AirportLocation("HWO", "Hollywood N. Perry", 26.0012, -80.2407),
        AirportLocation("LAL", "Lakeland Linder", 27.9889, -82.0186),
        AirportLocation("LEE", "Leesburg", 28.8234, -81.8093),
        AirportLocation("OCF", "Ocala", 29.1716, -82.2242),
        AirportLocation("SGJ", "St. Augustine", 29.9592, -81.3397),
        AirportLocation("ISM", "Orlando Kissimmee", 28.2898, -81.4371),
        AirportLocation("ORL", "Orlando Executive", 28.5455, -81.3329),
        AirportLocation("SPG", "Albert Whitted (St. Pete)", 27.7651, -82.6270),
        AirportLocation("CHN", "Wauchula", 27.5149, -81.8804),

        // More US - Southeast
        AirportLocation("SAV", "Savannah", 32.1276, -81.2021),
        AirportLocation("CHS", "Charleston", 32.8986, -80.0405),
        AirportLocation("MYR", "Myrtle Beach", 33.6797, -78.9283),
        AirportLocation("GSP", "Greenville-Spartanburg", 34.8957, -82.2189),
        AirportLocation("GSO", "Greensboro", 36.0978, -79.9373),
        AirportLocation("ORF", "Norfolk", 36.8946, -76.2012),
        AirportLocation("RIC", "Richmond", 37.5052, -77.3197),
        AirportLocation("BHM", "Birmingham", 33.5629, -86.7535),
        AirportLocation("HSV", "Huntsville", 34.6372, -86.7751),
        AirportLocation("MOB", "Mobile", 30.6912, -88.2428),
        AirportLocation("GPT", "Gulfport-Biloxi", 30.4073, -89.0701),
        AirportLocation("MEM", "Memphis", 35.0424, -89.9767),
        AirportLocation("LIT", "Little Rock", 34.7294, -92.2243),
        AirportLocation("SHV", "Shreveport", 32.4466, -93.8256),
        AirportLocation("BTR", "Baton Rouge", 30.5332, -91.1496),

        // More US - Northeast
        AirportLocation("ROC", "Rochester", 43.1189, -77.6724),
        AirportLocation("SYR", "Syracuse", 43.1112, -76.1063),
        AirportLocation("ALB", "Albany", 42.7483, -73.8017),
        AirportLocation("PVD", "Providence", 41.7326, -71.4204),
        AirportLocation("MHT", "Manchester NH", 42.9326, -71.4357),
        AirportLocation("PWM", "Portland ME", 43.6462, -70.3093),
        AirportLocation("BDL", "Hartford", 41.9389, -72.6832),
        AirportLocation("BUF", "Buffalo", 42.9405, -78.7322),
        AirportLocation("PIT", "Pittsburgh", 40.4915, -80.2329),

        // More US - Midwest
        AirportLocation("CLE", "Cleveland", 41.4117, -81.8498),
        AirportLocation("CMH", "Columbus", 39.9980, -82.8919),
        AirportLocation("IND", "Indianapolis", 39.7173, -86.2944),
        AirportLocation("MCI", "Kansas City", 39.2976, -94.7139),
        AirportLocation("CVG", "Cincinnati", 39.0488, -84.6678),
        AirportLocation("SDF", "Louisville", 38.1744, -85.7360),
        AirportLocation("MKE", "Milwaukee", 42.9472, -87.8966),
        AirportLocation("MSN", "Madison", 43.1399, -89.3375),
        AirportLocation("DSM", "Des Moines", 41.5340, -93.6631),
        AirportLocation("OMA", "Omaha", 41.3032, -95.8941),
        AirportLocation("GRR", "Grand Rapids", 42.8808, -85.5228),

        // More US - South Central
        AirportLocation("SAT", "San Antonio", 29.5337, -98.4698),
        AirportLocation("HOU", "Houston Hobby", 29.6454, -95.2789),
        AirportLocation("DAL", "Dallas Love Field", 32.8471, -96.8518),
        AirportLocation("OKC", "Oklahoma City", 35.3931, -97.6007),
        AirportLocation("TUL", "Tulsa", 36.1984, -95.8881),
        AirportLocation("ELP", "El Paso", 31.8072, -106.3778),
        AirportLocation("ABQ", "Albuquerque", 35.0402, -106.6090),

        // More US - West
        AirportLocation("SMF", "Sacramento", 38.6954, -121.5908),
        AirportLocation("SJC", "San Jose", 37.3626, -121.9291),
        AirportLocation("OAK", "Oakland", 37.7213, -122.2208),
        AirportLocation("BUR", "Burbank", 34.2007, -118.3585),
        AirportLocation("ONT", "Ontario CA", 34.0560, -117.6012),
        AirportLocation("PSP", "Palm Springs", 33.8297, -116.5067),
        AirportLocation("BOI", "Boise", 43.5644, -116.2228),
        AirportLocation("GEG", "Spokane", 47.6199, -117.5338),
        AirportLocation("RNO", "Reno", 39.4991, -119.7681),
        AirportLocation("TUS", "Tucson", 32.1161, -110.9410),
        AirportLocation("FAT", "Fresno", 36.7762, -119.7181),

        // More Europe
        AirportLocation("LGW", "London Gatwick", 51.1537, -0.1821),
        AirportLocation("STN", "London Stansted", 51.8850, 0.2350),
        AirportLocation("ORY", "Paris Orly", 48.7233, 2.3794),
        AirportLocation("ZRH", "Zurich", 47.4647, 8.5492),
        AirportLocation("VIE", "Vienna", 48.1103, 16.5697),
        AirportLocation("CPH", "Copenhagen", 55.6180, 12.6560),
        AirportLocation("BRU", "Brussels", 50.9014, 4.4844),
        AirportLocation("LIS", "Lisbon", 38.7813, -9.1359),
        AirportLocation("ARN", "Stockholm", 59.6519, 17.9186),
        AirportLocation("OSL", "Oslo", 60.1976, 11.1004),
        AirportLocation("HEL", "Helsinki", 60.3172, 24.9633),
        AirportLocation("ATH", "Athens", 37.9364, 23.9445),
        AirportLocation("PRG", "Prague", 50.1008, 14.2600),
        AirportLocation("WAW", "Warsaw", 52.1657, 20.9671),
        AirportLocation("MXP", "Milan Malpensa", 45.6306, 8.7281),
        AirportLocation("NAP", "Naples", 40.8860, 14.2908),

        // More Asia-Pacific
        AirportLocation("NRT", "Tokyo Narita", 35.7647, 140.3864),
        AirportLocation("PEK", "Beijing", 40.0799, 116.6031),
        AirportLocation("PVG", "Shanghai Pudong", 31.1434, 121.8052),
        AirportLocation("BKK", "Bangkok", 13.6900, 100.7501),
        AirportLocation("KUL", "Kuala Lumpur", 2.7456, 101.7099),
        AirportLocation("MEL", "Melbourne", -37.6690, 144.8410),
        AirportLocation("BNE", "Brisbane", -27.3842, 153.1175),
        AirportLocation("AKL", "Auckland", -37.0082, 174.7850),
        AirportLocation("DEL", "New Delhi", 28.5562, 77.1000),
        AirportLocation("BOM", "Mumbai", 19.0896, 72.8656),

        // More Middle East / Africa
        AirportLocation("AUH", "Abu Dhabi", 24.4330, 54.6511),
        AirportLocation("JED", "Jeddah", 21.6796, 39.1565),
        AirportLocation("CAI", "Cairo", 30.1219, 31.4056),
        AirportLocation("JNB", "Johannesburg", -26.1392, 28.2460),
        AirportLocation("ADD", "Addis Ababa", 8.9779, 38.7993),
        AirportLocation("NBO", "Nairobi", -1.3192, 36.9278),

        // South America
        AirportLocation("GRU", "Sao Paulo", -23.4356, -46.4731),
        AirportLocation("GIG", "Rio de Janeiro", -22.8100, -43.2506),
        AirportLocation("EZE", "Buenos Aires", -34.8222, -58.5358),
        AirportLocation("BOG", "Bogota", 4.7016, -74.1469),
        AirportLocation("LIM", "Lima", -12.0219, -77.1143),
        AirportLocation("SCL", "Santiago", -33.3930, -70.7858),
        AirportLocation("PTY", "Panama City", 9.0714, -79.3835),

        // Canada additional
        AirportLocation("YOW", "Ottawa", 45.3225, -75.6692),
        AirportLocation("YHZ", "Halifax", 44.8808, -63.5086),
        AirportLocation("YWG", "Winnipeg", 49.9100, -97.2399),
        AirportLocation("YEG", "Edmonton", 53.3097, -113.5800),

        // Canada
        AirportLocation("YYZ", "Toronto", 43.6777, -79.6248),
        AirportLocation("YVR", "Vancouver", 49.1967, -123.1815),
        AirportLocation("YUL", "Montreal", 45.4706, -73.7408),
        AirportLocation("YYC", "Calgary", 51.1215, -114.0076),

        // Mexico / Caribbean
        AirportLocation("MEX", "Mexico City", 19.4363, -99.0721),
        AirportLocation("CUN", "Cancun", 21.0365, -86.8771),
        AirportLocation("SJU", "San Juan", 18.4394, -66.0018),

        // Europe
        AirportLocation("LHR", "London Heathrow", 51.4700, -0.4543),
        AirportLocation("CDG", "Paris CDG", 49.0097, 2.5479),
        AirportLocation("FRA", "Frankfurt", 50.0379, 8.5622),
        AirportLocation("AMS", "Amsterdam", 52.3105, 4.7683),
        AirportLocation("MAD", "Madrid", 40.4983, -3.5676),
        AirportLocation("FCO", "Rome", 41.8003, 12.2389),
        AirportLocation("MUC", "Munich", 48.3538, 11.7861),
        AirportLocation("BCN", "Barcelona", 41.2971, 2.0785),
        AirportLocation("IST", "Istanbul", 41.2753, 28.7519),
        AirportLocation("DUB", "Dublin", 53.4264, -6.2499),

        // Middle East
        AirportLocation("DXB", "Dubai", 25.2532, 55.3657),
        AirportLocation("DOH", "Doha", 25.2731, 51.6081),

        // Asia-Pacific
        AirportLocation("HND", "Tokyo Haneda", 35.5494, 139.7798),
        AirportLocation("SIN", "Singapore", 1.3644, 103.9915),
        AirportLocation("HKG", "Hong Kong", 22.3080, 113.9185),
        AirportLocation("ICN", "Seoul Incheon", 37.4602, 126.4407),
        AirportLocation("SYD", "Sydney", -33.9461, 151.1772),
    )

    /** Get all airports with coordinates for map display. */
    fun getAirportsWithLocations(): List<AirportLocation> = airportLocations

    /**
     * Resolve an IATA (3-letter) or US ICAO (4-letter starting with K)
     * code to its [AirportLocation] entry, or null if not in the
     * database. Input is trimmed and uppercased, so "jfk", "KJFK", and
     * "JFK" all return the same entry.
     *
     * Used by the URL deep-link handler and the TopBar airport-code
     * search. Callers that only need the canonical code can read
     * `.code` off the result.
     */
    fun findLocation(code: String): AirportLocation? {
        val trimmed = code.trim().uppercase()
        airportLocations.firstOrNull { it.code == trimmed }?.let { return it }
        if (trimmed.length == 4 && trimmed.startsWith("K")) {
            val iata = trimmed.substring(1)
            return airportLocations.firstOrNull { it.code == iata }
        }
        return null
    }

    /**
     * Look up airport name from IATA or ICAO code.
     *
     * For US airports with ICAO codes starting with 'K', automatically strips the 'K'
     * and looks up the 3-letter IATA code (e.g., KSAN -> SAN).
     *
     * @param code IATA (3-letter) or ICAO (4-letter) airport code
     * @return Airport name, or the original code if not found
     */
    fun getName(code: String): String {
        val trimmed = code.trim().uppercase()

        // If it's a 4-letter US ICAO code (starts with K), strip the K and look up the 3-letter IATA code
        if (trimmed.length == 4 && trimmed.startsWith("K")) {
            val iataCode = trimmed.substring(1)
            airports[iataCode]?.let { return it }
        }

        // Look up in our database (handles direct IATA codes and non-US ICAO codes)
        return airports[trimmed] ?: trimmed
    }
}
